/**
 * Checks if a string is in the current domain
 * @param clearString - The string to check
 * @returns True if the string is in the current domain, false otherwise
 */
export const isInCurrentDomain = (clearString: string): boolean => {
	if (
		clearString.startsWith('//') && // Test that it's a valid domain first, to put it into the HTTP validation rule
		/(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9][a-z0-9-]{0,61}[a-z0-9]/g.test(
			`${location.protocol}:${clearString}`
		)
	) {
		clearString = `${location.protocol}${clearString}`
	}
	if (clearString.startsWith('http')) {
		// Test if it is the same hostname
		return new URL(clearString).hostname === location.hostname
	}
	return true
}

/**
 * Decodes a string and makes it lowercase and trim the start of the string
 * @param str - The string to decode
 * @returns The decoded string
 */
export const makeStringClear = (str: string) => {
	// need to remove
	return typeof str === 'string' ? decodeURIComponent(str).toLowerCase().trimStart() : str
}

const isCustomSetterAndGetter = (obj: { set?: Function; get?: Function } | any) => {
	return (
		obj?.set &&
		obj?.get &&
		typeof obj.set === 'function' &&
		typeof obj.get === 'function' &&
		!obj.get.toString().includes('[native code]') &&
		!obj.set.toString().includes('[native code]')
	)
}

// Define the named function for defining strict properties
const defineStrictProperty = (key: string, value: any, obj: any, enumerable: boolean) => {
	if (isCustomSetterAndGetter(value)) {
		Object.defineProperty(obj || globalThis, key, {
			get: value.get,
			set: value.set,
			configurable: false,
			enumerable: enumerable || false,
		})
	} else {
		Object.defineProperty(obj || globalThis, key, {
			value,
			writable: false,
			configurable: false,
			enumerable: enumerable || false,
		})
	}
}
/**
 * Reads a cookie from the document.cookie or from the cookie descriptor if provided
 * @param cookieName - The name of the cookie to read
 * @param cookie - The original cookie descriptor to use since we are overriding the cookie descriptor with limited access
 * @returns The value of the cookie or empty string if the cookie is not found
 */
export const readCookie = (cookieName: string, cookie?: PropertyDescriptor) => {
	try {
		const cookieHandle: string = cookie ? cookie!.get!.call(document) : document.cookie
		return cookieHandle.split(';')
			.map((_) => _.trim())
			.filter((currentCookie) => currentCookie?.startsWith(cookieName))[0]
			?.split('=')[1]
	} catch (e) {
		return ''
	}
}

/**
 * Builds an expired session cookie string
 * @param cookieName - The name of the cookie to build
 * @param domain - The domain of the cookie
 * @param path - The path of the cookie
 * @returns The expired session cookie string
 */
export const buildExpiredSessionCookie = (cookieName: string = '', domain: string = '', path: string = '/') =>
	`${cookieName}=; ${domain ? `domain=${domain};` : ''} max-age=0; path=${path}; expires=Thu, 01 Jan 1970 00:00:01 GMT`
